/**
 * SIGNATURE LOGO FILE — turn a practice's uploaded logo into an email-safe PNG.
 *
 * WHAT THIS IS
 *   The Settings -> Email -> Email Signature card lets an office UPLOAD a logo instead of
 *   pasting a URL. Practices hand us whatever their web designer gave them, which is very
 *   often a .webp or a .svg. This module validates that file, decides the target pixel size,
 *   and re-encodes it. The component then hands the returned File to the shared `uploadToS3`
 *   helper — the upload deliberately does NOT happen here (see "WHY THIS FILE IS
 *   IMPORT-FREE"); the component composes normalize -> upload.
 *
 * WHY THE OUTPUT IS ALWAYS PNG
 *   - Outlook desktop (the Word rendering engine) cannot render WebP at all. A .webp logo
 *     shows as a broken-image box in exactly the client dental offices email most.
 *   - Gmail and Outlook both BLOCK SVG in email outright: it is script-capable markup, so
 *     every major client strips it. An .svg logo is not "sometimes wrong", it is never shown.
 *   - JPEG has no alpha, so a logo drawn for a white page gets a grey or black box behind it
 *     the moment a client renders it on any other background.
 *   PNG is the only format every email client renders AND that keeps transparency, so we
 *   normalize everything to PNG rather than trying to guess which sources happen to be safe.
 *
 * WHY THE BOX IS 2x THE EMAIL BOX
 *   `emailSignature.js` LOGO_BOX is 200x80 — the size the img is DISPLAYED at in a
 *   signature. LOGO_UPLOAD_BOX is exactly 2x that (400x160) so the logo stays crisp on a
 *   retina/HiDPI screen, and no larger: at 3x the bytes roughly double again for no visible
 *   gain, and an email image that is slow to fetch is an email image that renders late.
 *   The factor being an exact integer multiple is asserted by the node test, so the two
 *   boxes cannot drift apart.
 *
 * WHY THE PREVIOUS S3 OBJECT IS NEVER DELETED
 *   Every logo upload writes a NEW S3 object and the settings blob starts pointing at it.
 *   The old object is left in place forever, on purpose: an email is not self-contained —
 *   the recipient's client fetches the logo from S3 when the message is OPENED, which can be
 *   months after it was sent. Deleting the object a previously-sent signature points at
 *   would retroactively break the logo in every email already sitting in a patient's inbox.
 *   Storage is cents; a year of sent mail with a broken image is not recoverable.
 *
 * WHY THIS FILE IS IMPORT-FREE
 *   `cypress/node-tests/signature-logo-file.test.js` LOADS AND EXECUTES this exact file
 *   (stripping the `export` keywords so Node can evaluate it) rather than mirroring it —
 *   the same technique as `src/utils/emailSignature.js` and `src/utils/phiMask.js`. A mirror
 *   can only prove the design; this proves the shipped code. That is also why every browser
 *   global is reached ONLY from inside `browserLogoDeps()` and never at module scope, and
 *   why the box maths below is duplicated from `emailSignature.js` instead of imported.
 */

/* -----------------------------------------------------------------------------
 * Constants
 * -------------------------------------------------------------------------- */

/** The `accept` attribute for the file input. Advisory only — real validation is below. */
export const ACCEPTED_LOGO_EXTENSIONS = '.png,.jpg,.jpeg,.gif,.webp,.svg';

/**
 * The MIME types we will decode. `image/jpg` is not a real IANA type but Windows and a few
 * older tools emit it, so it is accepted and normalized on the way through.
 */
export const ACCEPTED_LOGO_TYPES = Object.freeze([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
]);

/** 2x the 200x80 email box in emailSignature.js LOGO_BOX — see the header note on retina. */
export const LOGO_UPLOAD_BOX = Object.freeze({ maxW: 400, maxH: 160 });

/** 10 MB. Written longhand on purpose: numeric separators (10_000) break this repo's build. */
export const MAX_LOGO_FILE_BYTES = 10 * 1024 * 1024;

/**
 * 40 megapixels. A file can be tiny on disk and still enormous decoded (a 12000x12000 PNG of
 * flat colour compresses to a few hundred KB but needs ~576 MB of canvas memory), which is
 * how a "small" upload takes the browser tab down.
 */
export const MAX_SOURCE_PIXELS = 40000000;

/** Every practice's normalized logo is uploaded under this name. */
export const LOGO_UPLOAD_FILENAME = 'signature-logo.png';

/* User-facing copy. Plain English, ASCII only, short enough to read in a toast. */
const MSG_MISSING = 'Choose an image file.';
const MSG_HEIC = 'iPhone photos (HEIC) need to be exported as PNG or JPG first.';
const MSG_DESIGN = 'That is a design file. Export it as PNG and upload the PNG.';
const MSG_UNKNOWN = "That doesn't look like an image we can use. Try a PNG, JPG, GIF, WebP or SVG.";
const MSG_NO_SIZE = "We couldn't read that image's size. Try exporting it as PNG.";
const MSG_TOO_BIG = 'That image is very large - please use one under 6000px wide.';
const MSG_DECODE = "We couldn't read that image. Try exporting it as PNG.";
const MSG_CONVERT = "We couldn't convert that image. Try exporting it as PNG.";
const MSG_SVG_NO_SIZE = 'That SVG has no size. Export it as PNG instead.';

const EXTENSION_MIME = Object.freeze({
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.svg': 'image/svg+xml',
});

/** Formats a phone hands back that no browser can decode. */
const HEIC_TYPES = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'];
const HEIC_EXTS = ['.heic', '.heif'];

/** Print/design formats an office reaches for because "it's the logo file we were sent". */
const DESIGN_TYPES = [
  'application/pdf', 'application/postscript', 'application/illustrator',
  'application/x-photoshop', 'image/vnd.adobe.photoshop', 'image/x-eps',
];
const DESIGN_EXTS = ['.pdf', '.ai', '.eps', '.psd'];

/* -----------------------------------------------------------------------------
 * Small helpers
 * -------------------------------------------------------------------------- */

function lowerStr(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function extensionOf(name) {
  const s = lowerStr(name);
  const dot = s.lastIndexOf('.');
  return dot === -1 ? '' : s.slice(dot);
}

function has(list, value) {
  return !!value && list.indexOf(value) !== -1;
}

/* -----------------------------------------------------------------------------
 * File triage
 * -------------------------------------------------------------------------- */

/**
 * The MIME type to trust for a chosen file.
 *
 * The browser-reported `type` wins when it is one we accept, otherwise we fall back to the
 * filename extension. Windows reports `''` for .webp and .svg whenever no application is
 * registered for them, which is the common case on a front-desk machine — so the
 * extension branch is not an edge case we can skip.
 *
 * @param {string} name  the file name
 * @param {string} type  the browser-reported MIME type (often '')
 * @returns {string} an accepted MIME type, or '' when we cannot place the file
 */
export function inferLogoMime(name, type) {
  const t = lowerStr(type);
  if (has(ACCEPTED_LOGO_TYPES, t)) return t;
  const ext = extensionOf(name);
  if (Object.prototype.hasOwnProperty.call(EXTENSION_MIME, ext)) return EXTENSION_MIME[ext];
  return '';
}

/**
 * Why we cannot use this file, in plain English, or null when it is acceptable.
 *
 * The specific cases come first because they are the ones an office actually hits, and
 * "that doesn't look like an image" is useless advice for a HEIC photo or a PDF logo sheet:
 * the message has to say what to DO next.
 *
 * @param {{name:string,type:string,size:number}} file
 * @returns {string|null}
 */
export function describeLogoFileRejection(file) {
  if (!file || typeof file !== 'object') return MSG_MISSING;

  const type = lowerStr(file.type);
  const ext = extensionOf(file.name);

  if (has(HEIC_TYPES, type) || has(HEIC_EXTS, ext)) return MSG_HEIC;
  if (has(DESIGN_TYPES, type) || has(DESIGN_EXTS, ext)) return MSG_DESIGN;

  const size = Number(file.size);
  if (Number.isFinite(size) && size > MAX_LOGO_FILE_BYTES) {
    const mb = (size / (1024 * 1024)).toFixed(1);
    return `That file is ${mb} MB - the limit is 10 MB.`;
  }

  if (!inferLogoMime(file.name, file.type)) return MSG_UNKNOWN;
  return null;
}

/* -----------------------------------------------------------------------------
 * SVG intrinsic size
 * -------------------------------------------------------------------------- */

/**
 * Read a numeric SVG length.
 *
 * DELIBERATE CHOICE: only unitless and `px` values are honoured. Physical units (pt, mm, cm,
 * in) and font-relative units (em, ex, rem) need a DPI or a font context we do not have at
 * upload time, and guessing one silently produces a logo of the wrong size. Anything else
 * returns null so the caller falls back to `viewBox`, which is unitless by definition and is
 * the value the browser itself lays the graphic out in. Percentages are likewise "absent": a
 * percentage is relative to a container that does not exist here.
 */
function svgLength(value) {
  const raw = lowerStr(value);
  if (!raw || raw.slice(-1) === '%') return null;
  const m = /^([+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?)(px)?$/.exec(raw);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Value of `attr` inside a start tag: either quoting style, any attribute order. */
function tagAttr(tag, attr) {
  const re = new RegExp(`\\s${attr}\\s*=\\s*(?:"([^"]*)"|'([^']*)')`, 'i');
  const m = re.exec(tag);
  if (!m) return '';
  return m[1] !== undefined ? m[1] : m[2];
}

/**
 * The intrinsic pixel size of an SVG, read from the ROOT start tag by regex — no DOM, so
 * this runs unchanged in the node test and never parses someone else's markup into a live
 * DOM tree.
 *
 * width/height attributes win when both are usable; otherwise the last two numbers of
 * `viewBox` are used (space- or comma-separated, per the SVG grammar).
 *
 * @param {string} svgText
 * @returns {{width:number,height:number}|null} null when the size cannot be determined
 */
export function svgIntrinsicSize(svgText) {
  if (typeof svgText !== 'string' || !svgText) return null;
  const withoutComments = svgText.replace(/<!--[\s\S]*?-->/g, '');
  const tagMatch = /<svg(\s[^>]*)?>/i.exec(withoutComments);
  if (!tagMatch) return null;
  const tag = tagMatch[0];

  const w = svgLength(tagAttr(tag, 'width'));
  const h = svgLength(tagAttr(tag, 'height'));
  if (w && h) return { width: w, height: h };

  const viewBox = tagAttr(tag, 'viewBox');
  if (viewBox) {
    const parts = viewBox.trim().split(/[\s,]+/).map(Number);
    if (parts.length === 4 && parts.every((n) => Number.isFinite(n))) {
      const vw = parts[2];
      const vh = parts[3];
      if (vw > 0 && vh > 0) return { width: vw, height: vh };
    }
  }
  return null;
}

/* -----------------------------------------------------------------------------
 * Target canvas size
 * -------------------------------------------------------------------------- */

/**
 * The canvas size to render the logo at, or `{ error }` carrying a user-facing message.
 *
 * RASTER: scale DOWN only (`Math.min(1, ...)`), aspect preserved. This is the SAME six lines
 * as `fitLogoBox()` in `src/utils/emailSignature.js` (~line 187), against LOGO_UPLOAD_BOX
 * instead of LOGO_BOX. Duplicated on purpose — this file must stay import-free so the node
 * test can execute it — and `cypress/node-tests/signature-logo-file.test.js` pins the two
 * boxes to a fixed integer ratio so the duplication cannot silently drift.
 *
 * VECTOR: no `Math.min(1, ...)` cap. An SVG is resolution-independent, so a 100x40 icon is
 * rasterized at the full 400x160 rather than left at 100x40 and looking soft.
 *
 * @param {{width:number,height:number,isVector:boolean}} input
 * @returns {{width:number,height:number}|{error:string}}
 */
export function planLogoRaster(input) {
  const src = input || {};
  const w = Number(src.width);
  const h = Number(src.height);
  const isVector = !!src.isVector;

  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return { error: MSG_NO_SIZE };
  }
  if (!isVector && w * h > MAX_SOURCE_PIXELS) {
    return { error: MSG_TOO_BIG };
  }

  const box = LOGO_UPLOAD_BOX;
  const scale = isVector
    ? Math.min(box.maxW / w, box.maxH / h)
    : Math.min(1, box.maxW / w, box.maxH / h);

  return {
    width: Math.max(1, Math.round(w * scale)),
    height: Math.max(1, Math.round(h * scale)),
  };
}

/* -----------------------------------------------------------------------------
 * URL predicates the settings screen uses
 * -------------------------------------------------------------------------- */

/**
 * True when a logo URL points at a format email clients cannot be trusted to render —
 * WebP (Outlook desktop shows a broken image) or SVG (Gmail and Outlook block it outright).
 * Only the PATH counts: `?format=webp` on a CDN URL says nothing about the bytes returned.
 *
 * @param {string} url
 * @returns {boolean}
 */
export function looksLikeEmailUnsafeImageUrl(url) {
  if (typeof url !== 'string' || !url) return false;
  const path = url.split('#')[0].split('?')[0];
  return /\.(webp|svg)$/i.test(path);
}

/**
 * True when the URL is one WE produced through the shared upload path, so the settings
 * screen can tell "the logo this office uploaded here" from "a URL they pasted off their
 * own website". Both the dev and prod buckets; https only.
 *
 * @param {string} url
 * @returns {boolean}
 */
export function isTeamioUploadUrl(url) {
  if (typeof url !== 'string' || !url) return false;
  return /^https:\/\/s3\.amazonaws\.com\/teamio-clients(-prod)?\/forms\//.test(url);
}

/* -----------------------------------------------------------------------------
 * The normalizer
 * -------------------------------------------------------------------------- */

/**
 * Validate, decode, downscale and re-encode a chosen file as a PNG fitting LOGO_UPLOAD_BOX.
 *
 * Every browser API this needs arrives through `deps` (see `browserLogoDeps()`), which is
 * what lets the node test drive the real algorithm with plain objects on a Node process that
 * has no DOM at all.
 *
 * @param {{name:string,type:string,size:number}} file
 * @param {object} deps  readText, makeBlob, makeFile, decodeImage, makeCanvas,
 *                       canvasToBlob, release
 * @returns {Promise<{file:object,width:number,height:number}>}
 * @throws {Error} whose `.message` is safe to show the user verbatim
 */
export async function normalizeLogoFile(file, deps) {
  const d = deps || {};

  const rejection = describeLogoFileRejection(file);
  if (rejection) throw new Error(rejection);

  const mime = inferLogoMime(file.name, file.type);
  const isVector = mime === 'image/svg+xml';

  let source = file;
  let vectorSize = null;

  if (isVector) {
    const text = await d.readText(file);
    vectorSize = svgIntrinsicSize(text);
    if (!vectorSize) throw new Error(MSG_SVG_NO_SIZE);
    // Re-wrap with an explicit image/svg+xml type: image decoding byte-sniffs PNG, JPEG,
    // GIF and WebP but NOT SVG, so an SVG handed over with a blank type never decodes.
    source = d.makeBlob([text], 'image/svg+xml');
  }

  let img = null;
  try {
    img = await d.decodeImage(source);

    // For a vector use the PARSED size: Firefox reports naturalWidth/naturalHeight of 0x0
    // for a viewBox-only SVG, which would look like a decode failure and reject a good file.
    const width = isVector ? vectorSize.width : (img && img.naturalWidth);
    const height = isVector ? vectorSize.height : (img && img.naturalHeight);
    if (!isVector && !(width > 0 && height > 0)) throw new Error(MSG_DECODE);

    const plan = planLogoRaster({ width, height, isVector });
    if (plan.error) throw new Error(plan.error);

    const canvas = d.makeCanvas(plan.width, plan.height);
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    try {
      ctx.drawImage(img, 0, 0, plan.width, plan.height);
    } catch (e) {
      // An SVG carrying a foreignObject or an external image taints the canvas (and throws
      // outright in Firefox). Nothing to salvage — tell them to export a PNG.
      throw new Error(MSG_CONVERT);
    }

    const blob = await d.canvasToBlob(canvas, 'image/png');
    if (!blob) throw new Error(MSG_CONVERT);

    return {
      file: d.makeFile(blob, LOGO_UPLOAD_FILENAME, 'image/png'),
      width: plan.width,
      height: plan.height,
    };
  } finally {
    // Every path after a successful decode frees the decoded image — in the browser this is
    // where the object URL is revoked, and a leak here is a leak per upload attempt.
    if (img && d.release) d.release(img);
  }
}

/**
 * The real browser implementations. Globals are touched ONLY when this is CALLED, never at
 * module scope, so the node test can load this file on a plain Node process.
 *
 * Decoding goes through an image element rather than `createImageBitmap`: the bitmap API's
 * EXIF-orientation default is the known trap (a photo-derived logo comes out sideways),
 * while image-element decoding applies EXIF orientation in every modern browser.
 *
 * @returns {object} the deps object `normalizeLogoFile` expects
 */
export function browserLogoDeps() {
  return {
    readText: (file) => file.text(),

    makeBlob: (parts, type) => new Blob(parts, { type }),

    makeFile: (blob, name, type) => new File([blob], name, { type }),

    decodeImage: (blob) => new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(blob);
      const img = new Image();
      img.__objectUrl = objectUrl;
      img.onload = () => resolve(img);
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error(MSG_DECODE));
      };
      img.src = objectUrl;
    }),

    makeCanvas: (w, h) => {
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      return canvas;
    },

    canvasToBlob: (canvas, type) => new Promise((resolve) => { canvas.toBlob(resolve, type); }),

    release: (img) => {
      if (img && img.__objectUrl) URL.revokeObjectURL(img.__objectUrl);
    },
  };
}
